import request from '@/utils/request'
import {
  buildTourLeaderJobQuery,
  requireApplicationStatus,
  requireJobStatus,
  toTourLeaderJobPayload,
} from '@/utils/tourLeaderJob'

const adminApiBase = '/tour-leader-admin/v1'

function adminRequest(config) {
  return request({
    ...config,
    preserveHttpResponse: true,
  })
}

function pickDefined(source, fields) {
  return fields.reduce((payload, field) => {
    if (source && source[field] !== undefined) payload[field] = source[field]
    return payload
  }, {})
}

function clampPage(value, fallback = 1) {
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback
}

function requirePathSegment(value, errorCode) {
  const normalized = String(value === undefined || value === null ? '' : value).trim()
  if (!normalized) throw new TypeError(errorCode)
  return encodeURIComponent(normalized)
}

export function getTourLeaderSettings(params) {
  return adminRequest({
    url: `${adminApiBase}/settings`,
    method: 'get',
    params: {
      keyword: String((params && params.keyword) || '')
        .trim()
        .slice(0, 100),
      page: clampPage(params && params.page),
      pageSize: Math.min(100, clampPage(params && (params.pageSize || params.limit), 20)),
    },
  })
}

export function updateTourLeaderSetting(agencyCode, data) {
  return adminRequest({
    url: `${adminApiBase}/settings/${requirePathSegment(
      agencyCode,
      'tour_leader_setting_agency_required'
    )}`,
    method: 'put',
    data: { isEnabled: Boolean(data && data.isEnabled) },
  })
}

export function updateTourLeaderProfileRecommendation(userId, data) {
  const parsedUserId = Number(userId)
  if (!Number.isInteger(parsedUserId) || parsedUserId <= 0) {
    throw new TypeError('tour_leader_profile_user_invalid')
  }
  return adminRequest({
    url: `${adminApiBase}/profiles/${encodeURIComponent(parsedUserId)}/recommendation`,
    method: 'put',
    data: {
      isRecommended: Boolean(data && data.isRecommended),
      recommendSort: requireIntegerInRange(
        Number(data && data.recommendSort),
        0,
        9999,
        'tour_leader_profile_recommend_sort_invalid'
      ),
    },
  })
}

export function getTourLeaderBanners() {
  return adminRequest({
    url: `${adminApiBase}/banners`,
    method: 'get',
  })
}

function requirePayloadFields(payload, fields) {
  const missing = fields.some(
    field => payload[field] === undefined || payload[field] === null || payload[field] === ''
  )
  if (missing) throw new TypeError('tour_leader_admin_job_contact_fields_required')
  const travelId = Number(payload.travelId)
  if (!Number.isInteger(travelId) || travelId <= 0) {
    throw new TypeError('tour_leader_admin_job_contact_travel_invalid')
  }
  payload.travelId = travelId
  return payload
}

function applicationPath(jobId, applicationId) {
  return `${adminApiBase}/jobs/${encodeURIComponent(jobId)}/applications/${encodeURIComponent(
    applicationId
  )}`
}

export function getAdminJobs(params) {
  return adminRequest({
    url: `${adminApiBase}/jobs`,
    method: 'get',
    params: buildTourLeaderJobQuery(params, {
      includeStatus: true,
      includeAgency: true,
    }),
  })
}

export function getAdminJob(jobId) {
  return adminRequest({
    url: `${adminApiBase}/jobs/${encodeURIComponent(jobId)}`,
    method: 'get',
  })
}

export function createAdminJob(data) {
  return adminRequest({
    url: `${adminApiBase}/jobs`,
    method: 'post',
    data: toTourLeaderJobPayload(data, {
      includeAdminFields: true,
      includeTargetAgency: true,
    }),
  })
}

export function updateAdminJob(jobId, data) {
  return adminRequest({
    url: `${adminApiBase}/jobs/${encodeURIComponent(jobId)}`,
    method: 'put',
    data: toTourLeaderJobPayload(data, { includeAdminFields: true }),
  })
}

export function deleteAdminJob(jobId) {
  return adminRequest({
    url: `${adminApiBase}/jobs/${encodeURIComponent(jobId)}`,
    method: 'delete',
  })
}

export function copyAdminJob(jobId) {
  return adminRequest({
    url: `${adminApiBase}/jobs/${encodeURIComponent(jobId)}/copy`,
    method: 'post',
  })
}

export function updateAdminJobStatus(jobId, status) {
  return adminRequest({
    url: `${adminApiBase}/jobs/${encodeURIComponent(jobId)}/status`,
    method: 'put',
    data: { status: requireJobStatus(status) },
  })
}

export function getAdminJobApplications(jobId, params) {
  return adminRequest({
    url: `${adminApiBase}/jobs/${encodeURIComponent(jobId)}/applications`,
    method: 'get',
    params: {
      page: Math.max(1, Number(params && params.page) || 1),
      pageSize: Math.min(
        100,
        Math.max(1, Number(params && (params.pageSize || params.limit)) || 20)
      ),
    },
  })
}

export function updateAdminJobApplicationStatus(jobId, applicationId, status) {
  return adminRequest({
    url: `${adminApiBase}/jobs/${encodeURIComponent(jobId)}/applications/${encodeURIComponent(
      applicationId
    )}/status`,
    method: 'put',
    data: { status: requireApplicationStatus(status) },
  })
}

export function getAdminJobApplicationTravels(jobId, applicationId) {
  return adminRequest({
    url: `${applicationPath(jobId, applicationId)}/travels`,
    method: 'get',
  })
}

export function createAdminJobApplicationInvitation(jobId, applicationId, data) {
  const payload = requirePayloadFields(
    pickDefined(data, [
      'travelId',
      'title',
      'content',
      'workStartAt',
      'workEndAt',
      'expiresAt',
      'clientMessageId',
    ]),
    ['travelId', 'title', 'content', 'expiresAt', 'clientMessageId']
  )
  payload.title = String(payload.title).trim().slice(0, 200)
  payload.content = String(payload.content).trim().slice(0, 4000)
  if (!payload.title || !payload.content) {
    throw new TypeError('tour_leader_admin_job_contact_fields_required')
  }
  return adminRequest({
    url: `${applicationPath(jobId, applicationId)}/invitation`,
    method: 'post',
    data: payload,
  })
}

export function createAdminJobApplicationConversation(jobId, applicationId, data) {
  const payload = requirePayloadFields(
    pickDefined(data, [
      'travelId',
      'messageType',
      'content',
      'mediaId',
      'invitationId',
      'clientMessageId',
    ]),
    ['travelId', 'messageType', 'content', 'clientMessageId']
  )
  payload.messageType = 'text'
  payload.content = String(payload.content).trim().slice(0, 4000)
  if (!payload.content) throw new TypeError('tour_leader_admin_job_contact_fields_required')
  return adminRequest({
    url: `${applicationPath(jobId, applicationId)}/conversation`,
    method: 'post',
    data: payload,
  })
}

export function getDiscussions(params) {
  return request({
    url: `${adminApiBase}/discussions`,
    method: 'get',
    params: {
      keyword: String((params && params.keyword) || '').trim(),
      visibility: String((params && params.visibility) || '').trim(),
      page: Math.max(1, Number(params && params.page) || 1),
      pageSize: Math.min(
        100,
        Math.max(1, Number(params && (params.pageSize || params.limit)) || 20)
      ),
    },
  })
}

export function updateDiscussionVisibility(postId, hidden, reason) {
  return request({
    url: `${adminApiBase}/discussions/${encodeURIComponent(postId)}/visibility`,
    method: 'put',
    data: { hidden: hidden === true, reason: String(reason || '').trim() },
  })
}

export function getDiscussionComments(postId, params) {
  return request({
    url: `${adminApiBase}/discussions/${encodeURIComponent(postId)}/comments`,
    method: 'get',
    params: {
      page: Math.max(1, Number(params && params.page) || 1),
      pageSize: Math.min(
        100,
        Math.max(1, Number(params && (params.pageSize || params.limit)) || 20)
      ),
    },
  })
}

export function updateDiscussionCommentVisibility(commentId, hidden, reason) {
  return request({
    url: `${adminApiBase}/discussion-comments/${encodeURIComponent(commentId)}/visibility`,
    method: 'put',
    data: { hidden: hidden === true, reason: String(reason || '').trim() },
  })
}

export function getDiscussionReports(params) {
  return request({
    url: `${adminApiBase}/discussion-reports`,
    method: 'get',
    params: {
      status: String((params && params.status) || '').trim(),
      page: Math.max(1, Number(params && params.page) || 1),
      pageSize: Math.min(
        100,
        Math.max(1, Number(params && (params.pageSize || params.limit)) || 20)
      ),
    },
  })
}

export function resolveDiscussionReport(reportId, data) {
  return request({
    url: `${adminApiBase}/discussion-reports/${encodeURIComponent(reportId)}/resolution`,
    method: 'put',
    data: {
      status: String((data && data.status) || '').trim(),
      note: String((data && data.note) || '').trim(),
    },
  })
}

export function getIncomeOpportunities(params) {
  return request({
    url: `${adminApiBase}/income-opportunities`,
    method: 'get',
    params: {
      keyword: String((params && params.keyword) || '').trim(),
      status: String((params && params.status) || '').trim(),
      page: Math.max(1, Number(params && params.page) || 1),
      pageSize: Math.min(
        100,
        Math.max(1, Number(params && (params.pageSize || params.limit)) || 20)
      ),
    },
  })
}

export function createIncomeOpportunity(data) {
  return request({
    url: `${adminApiBase}/income-opportunities`,
    method: 'post',
    data: toIncomeOpportunityPayload(data),
  })
}

export function getIncomeOpportunity(opportunityId) {
  return request({
    url: incomeOpportunityPath(opportunityId),
    method: 'get',
  })
}

export function updateIncomeOpportunity(opportunityId, data) {
  return request({
    url: incomeOpportunityPath(opportunityId),
    method: 'put',
    data: toIncomeOpportunityPayload(data),
  })
}

export function updateIncomeOpportunityStatus(opportunityId, status) {
  return request({
    url: `${incomeOpportunityPath(opportunityId)}/status`,
    method: 'put',
    data: { status: requireIncomeStatus(status) },
  })
}

export function updateIncomeOpportunityRecommendation(opportunityId, isRecommended, recommendSort) {
  return request({
    url: `${incomeOpportunityPath(opportunityId)}/recommendation`,
    method: 'put',
    data: {
      isRecommended: isRecommended === true,
      recommendSort: clampInteger(recommendSort, 0, 9999, 99),
    },
  })
}

export function copyIncomeOpportunity(opportunityId) {
  return request({
    url: `${incomeOpportunityPath(opportunityId)}/copy`,
    method: 'post',
  })
}

export function deleteIncomeOpportunity(opportunityId) {
  return request({
    url: incomeOpportunityPath(opportunityId),
    method: 'delete',
  })
}

export function getIncomeApplications(opportunityId, params) {
  return request({
    url: `${incomeOpportunityPath(opportunityId)}/applications`,
    method: 'get',
    params: toIncomePageQuery(params, true, false),
  })
}

export function updateIncomeApplicationStatus(opportunityId, applicationId, status) {
  return request({
    url: `${incomeOpportunityPath(opportunityId)}/applications/${encodeURIComponent(
      applicationId
    )}/status`,
    method: 'put',
    data: { status: requireIncomeApplicationStatus(status) },
  })
}

export function getIncomeEarnings(params) {
  return request({
    url: `${adminApiBase}/income-earnings`,
    method: 'get',
    params: {
      ...toIncomePageQuery(params, true, true),
      claimStatus: String((params && params.claimStatus) || '').trim(),
    },
  })
}

export function createIncomeEarning(opportunityId, data) {
  return request({
    url: `${incomeOpportunityPath(opportunityId)}/earnings`,
    method: 'post',
    data: toIncomeEarningPayload(data, true),
  })
}

export function updateIncomeEarning(earningId, data) {
  return request({
    url: `${adminApiBase}/income-earnings/${encodeURIComponent(earningId)}`,
    method: 'put',
    data: toIncomeEarningPayload(data, true),
  })
}

export function deleteIncomeEarning(earningId) {
  return request({
    url: `${adminApiBase}/income-earnings/${encodeURIComponent(earningId)}`,
    method: 'delete',
  })
}

export function updateIncomeEarningClaimStatus(earningId, status, note) {
  const normalizedNote = String(note || '').trim()
  if (normalizedNote.length < 2 || normalizedNote.length > 500) {
    throw new TypeError('tour_leader_income_claim_note_invalid')
  }
  return request({
    url: `${adminApiBase}/income-earnings/${encodeURIComponent(earningId)}/claim-status`,
    method: 'put',
    data: {
      status: requireIncomeClaimStatus(status),
      note: normalizedNote,
    },
  })
}

const incomeStatuses = ['draft', 'published', 'unpublished', 'closed']
const incomeApplicationStatuses = ['reviewing', 'approved', 'rejected', 'cancelled']
const incomeClaimStatuses = ['approved', 'rejected']

function clampInteger(value, min, max, fallback) {
  const parsed = Number(value)
  return Number.isInteger(parsed) ? Math.min(max, Math.max(min, parsed)) : fallback
}

function requireIntegerInRange(value, min, max, errorCode) {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < min || value > max) {
    throw new TypeError(errorCode)
  }
  return value
}

function requireEnum(value, allowedValues, errorCode) {
  const match = allowedValues.find(item => item.toLowerCase() === String(value || '').toLowerCase())
  if (!match) throw new TypeError(errorCode)
  return match
}

function requireIncomeStatus(status) {
  return requireEnum(status, incomeStatuses, 'tour_leader_income_status_invalid')
}

function requireIncomeApplicationStatus(status) {
  return requireEnum(
    status,
    incomeApplicationStatuses,
    'tour_leader_income_application_status_invalid'
  )
}

function requireIncomeClaimStatus(status) {
  return requireEnum(status, incomeClaimStatuses, 'tour_leader_income_claim_status_invalid')
}

function incomeOpportunityPath(opportunityId) {
  return `${adminApiBase}/income-opportunities/${encodeURIComponent(opportunityId)}`
}

function toIncomePageQuery(params, includeStatus, includeKeyword) {
  const query = {
    page: Math.max(1, Number(params && params.page) || 1),
    pageSize: Math.min(100, Math.max(1, Number(params && (params.pageSize || params.limit)) || 20)),
  }
  if (includeKeyword) query.keyword = String((params && params.keyword) || '').trim()
  if (includeStatus) query.status = String((params && params.status) || '').trim()
  return query
}

function toIncomeOpportunityPayload(source) {
  const payload = pickDefined(source, [
    'title',
    'partnerName',
    'category',
    'eligibleRole',
    'workMode',
    'rewardDescription',
    'summary',
    'workItems',
    'requirements',
    'quota',
    'applicationDeadline',
    'alwaysOpen',
    'isRecommended',
    'recommendSort',
    'tags',
  ])

  const textLimits = {
    title: 120,
    partnerName: 100,
    category: 50,
    workMode: 50,
    rewardDescription: 200,
    summary: 1000,
    workItems: 2000,
    requirements: 2000,
  }
  Object.keys(textLimits).forEach(field => {
    if (payload[field] !== undefined) {
      payload[field] = String(payload[field]).trim().slice(0, textLimits[field])
    }
  })
  if (payload.eligibleRole !== undefined) {
    payload.eligibleRole = requireEnum(
      payload.eligibleRole,
      ['leader', 'guide', 'both'],
      'tour_leader_income_eligible_role_invalid'
    )
  }
  if (payload.quota !== undefined) {
    payload.quota = requireIntegerInRange(
      payload.quota,
      0,
      999999,
      'tour_leader_income_quota_invalid'
    )
  }
  if (payload.recommendSort !== undefined) {
    payload.recommendSort = clampInteger(payload.recommendSort, 0, 9999, 99)
  }
  if (payload.alwaysOpen !== undefined) payload.alwaysOpen = payload.alwaysOpen === true
  if (payload.applicationDeadline !== undefined) {
    payload.applicationDeadline = payload.alwaysOpen
      ? ''
      : String(payload.applicationDeadline || '')
          .trim()
          .slice(0, 10)
  }
  if (payload.isRecommended !== undefined) payload.isRecommended = payload.isRecommended === true
  if (payload.tags !== undefined) {
    if (!Array.isArray(payload.tags) || payload.tags.length > 8) {
      throw new TypeError('tour_leader_income_tags_invalid')
    }
    payload.tags = payload.tags.map(tag => String(tag).trim())
    if (payload.tags.some(tag => tag.length < 1 || tag.length > 30)) {
      throw new TypeError('tour_leader_income_tags_invalid')
    }
  }
  return payload
}

function toIncomeEarningPayload(source, includeIdentity) {
  const fields = [
    'title',
    'amount',
    'currency',
    'description',
    'status',
    'completedAt',
    'settlementDate',
  ]
  if (includeIdentity) fields.unshift('applicationId', 'userId')
  const payload = pickDefined(source, fields)
  if (payload.userId !== undefined) {
    payload.userId = Number(payload.userId)
    if (!Number.isInteger(payload.userId) || payload.userId <= 0) {
      throw new TypeError('tour_leader_income_earning_user_invalid')
    }
  }
  if (payload.amount !== undefined) {
    payload.amount = Number(payload.amount)
    if (
      !Number.isFinite(payload.amount) ||
      payload.amount < 0.01 ||
      payload.amount > 9999999999.99
    ) {
      throw new TypeError('tour_leader_income_earning_amount_invalid')
    }
  }
  if (payload.title !== undefined) payload.title = String(payload.title).trim().slice(0, 200)
  if (payload.currency !== undefined) {
    payload.currency = String(payload.currency).trim().toUpperCase().slice(0, 3)
    if (!/^[A-Z]{3}$/.test(payload.currency)) {
      throw new TypeError('tour_leader_income_earning_currency_invalid')
    }
  }
  if (payload.description !== undefined) {
    payload.description = String(payload.description).trim().slice(0, 1000)
  }
  if (payload.status !== undefined) {
    payload.status = requireEnum(
      payload.status,
      ['pending', 'available'],
      'tour_leader_income_earning_status_invalid'
    )
  }
  if (payload.completedAt !== undefined) {
    const completedAt = payload.completedAt === null ? '' : String(payload.completedAt).trim()
    payload.completedAt = completedAt || null
  }
  if (payload.settlementDate !== undefined) {
    const settlementDate =
      payload.settlementDate === null ? '' : String(payload.settlementDate).trim()
    payload.settlementDate = settlementDate ? settlementDate.slice(0, 10) : null
  }
  return payload
}
